/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { LoadingSpinner } from "../loading_spinner";
import { StyledAddButton, StyledCancelButton } from "../../pages/restaurant_groups/styles";
import { DEFAULT_EXPORT_FORMAT, EXPORT_FORMATS } from "../../utils/reportExport";
import {
  exportDialogActionsStyles,
  exportDialogDescriptionStyles,
  exportDialogTitleStyles,
  exportRadioStyles,
} from "./styles";

/**
 * The format picker every report export button opens, following the Perq Cards export dialog:
 * one radio per format, Cancel and Download. Shared so the two Sales reports cannot end up with
 * different affordances for the same choice.
 *
 * Presentational — it owns the chosen format and nothing else. The caller runs the download
 * and decides whether the dialog closes, so a failed export keeps the choice on screen.
 */
export const ReportExportDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  loading = false,
  testId,
}) => {
  const [format, setFormat] = useState(DEFAULT_EXPORT_FORMAT);

  useEffect(() => {
    if (open) setFormat(DEFAULT_EXPORT_FORMAT);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
    >
      <DialogTitle id={`${testId}-title`} sx={exportDialogTitleStyles}>
        {title}
      </DialogTitle>

      <DialogContent>
        {description ? (
          <Typography sx={exportDialogDescriptionStyles} data-testid={`${testId}-description`}>
            {description}
          </Typography>
        ) : null}

        <RadioGroup
          aria-label="Export format"
          value={format}
          onChange={(event) => setFormat(event.target.value)}
        >
          {EXPORT_FORMATS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              label={option.label}
              disabled={loading}
              control={
                <Radio
                  sx={exportRadioStyles}
                  inputProps={{ "data-testid": `${testId}-format-${option.value}` }}
                />
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={exportDialogActionsStyles}>
        <StyledCancelButton
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ textTransform: "none", borderRadius: "8px" }}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </StyledCancelButton>
        <StyledAddButton
          onClick={() => onConfirm(format)}
          variant="contained"
          disabled={loading}
          aria-label="Download"
          sx={{ padding: "6px 16px !important" }}
          data-testid={`${testId}-confirm`}
        >
          {loading ? <LoadingSpinner isLoading={true} /> : "Download"}
        </StyledAddButton>
      </DialogActions>
    </Dialog>
  );
};
